/**
 * Cloud Folder Linker Component
 * Modal for selecting a cloud folder to link for auto-sync.
 * Remembers the last browsed location per provider.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spin } from 'antd';
import { useLanguage } from '../../../hooks/useLanguage';
import { CloudStorageApi } from '../services/cloudStorageService';
import { useCloudConnections } from '../hooks/useCloudConnections';
import { GoogleAccessPrompt } from '@/modules/cloud-storage/components/GoogleAccessPrompt';
import { useGoogleReadGate } from '@/modules/cloud-storage/hooks/useGoogleReadGate';
import type { CloudFolder } from '../types';
import './CloudFilePicker.css'; // Reuses the cloud picker styles

interface CloudFolderLinkerProps {
  visible: boolean;
  onSelect: (provider: string, folderId: string, folderName: string, folderPath?: string) => void;
  onCancel: () => void;
}

interface BreadcrumbItem {
  id: string | null;
  name: string;
}

const PROVIDERS = [
  { key: 'google_drive', label: 'Google Drive' },
  { key: 'dropbox', label: 'Dropbox' },
];

export const CloudFolderLinker: React.FC<CloudFolderLinkerProps> = ({
  visible,
  onSelect,
  onCancel,
}) => {
  const { t } = useLanguage();
  const { connections, loadConnections, isConnected } = useCloudConnections();
  const [activeProvider, setActiveProvider] = useState<string>('');
  const [folders, setFolders] = useState<CloudFolder[]>([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<CloudFolder | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  // Google Drive lists the person's own folders only with Google's `read`
  // level (drive.readonly), asked for here the first time it is needed.
  const gate = useGoogleReadGate(visible && activeProvider === 'google_drive');
  const { pending: accessPending, needsRead } = gate;
  const defaultAppliedFor = useRef<string>('');

  useEffect(() => {
    if (visible) {
      loadConnections();
      setSelectedFolder(null);
      setBreadcrumbs([{ id: null, name: 'Root' }]);
      setCurrentFolderId(null);
      setFolders([]);
      setActiveProvider('');
      defaultAppliedFor.current = '';
    }
  }, [visible, loadConnections]);

  // Auto-select first connected provider
  useEffect(() => {
    if (visible && connections.length > 0 && !activeProvider) {
      const firstConnected = PROVIDERS.find((p) => isConnected(p.key));
      if (firstConnected) {
        setActiveProvider(firstConnected.key);
      }
    }
  }, [visible, connections, activeProvider, isConnected]);

  // Load saved default folder when provider activates
  useEffect(() => {
    if (!activeProvider || !visible || accessPending || needsRead) return;
    if (defaultAppliedFor.current === activeProvider) return;
    defaultAppliedFor.current = activeProvider;

    CloudStorageApi.getDefaultExportFolder(activeProvider)
      .then((saved) => {
        if (saved && saved.breadcrumbs?.length) {
          setBreadcrumbs(saved.breadcrumbs);
          setCurrentFolderId(saved.folder_id || null);
        } else {
          setBreadcrumbs([{ id: null, name: 'Root' }]);
          setCurrentFolderId(null);
        }
      })
      .catch(() => {
        setBreadcrumbs([{ id: null, name: 'Root' }]);
        setCurrentFolderId(null);
      });
  }, [activeProvider, visible, accessPending, needsRead]);

  // Load folder contents when provider or folder changes
  useEffect(() => {
    if (activeProvider && visible && !accessPending && !needsRead) {
      loadFolders(activeProvider, currentFolderId);
    }
  }, [activeProvider, currentFolderId, visible, accessPending, needsRead]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFolders = useCallback(
    async (provider: string, parentId: string | null) => {
      setLoadingContent(true);
      try {
        const folderList = await CloudStorageApi.listFolders(provider, parentId || undefined);
        setFolders(folderList);
      } catch (error) {
        if (!gate.refusedBy(error)) console.error('Error loading cloud folders:', error);
        setFolders([]);
      } finally {
        setLoadingContent(false);
      }
    },
    []
  );

  const saveCurrentPosition = (provider: string) => {
    CloudStorageApi.setDefaultExportFolder(
      provider,
      currentFolderId || '',
      breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 1].name : 'Root',
      breadcrumbs,
    ).catch(() => {});
  };

  const handleProviderChange = (providerKey: string) => {
    setActiveProvider(providerKey);
    setSelectedFolder(null);
    defaultAppliedFor.current = '';
  };

  const handleFolderNavigate = (folder: CloudFolder) => {
    setCurrentFolderId(folder.folder_id);
    setBreadcrumbs((prev) => [...prev, { id: folder.folder_id, name: folder.name }]);
    setSelectedFolder(null);
  };

  const handleBreadcrumbClick = (index: number) => {
    const item = breadcrumbs[index];
    setCurrentFolderId(item.id);
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
    setSelectedFolder(null);
  };

  const handleFolderSelect = (folder: CloudFolder) => {
    setSelectedFolder((prev) =>
      prev?.folder_id === folder.folder_id ? null : folder
    );
  };

  const handleConfirm = () => {
    if (selectedFolder) {
      saveCurrentPosition(activeProvider);
      const folderPath = breadcrumbs
        .slice(1)
        .map((b) => b.name)
        .concat(selectedFolder.name)
        .join('/');
      onSelect(activeProvider, selectedFolder.folder_id, selectedFolder.name, folderPath);
    }
  };

  if (!visible) return null;

  const connectedProviders = PROVIDERS.filter((p) => isConnected(p.key));

  return (
    <div className="cloud-picker-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="cloud-picker-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cloud-picker-header">
          <div>
            <h2 className="cloud-picker-title">{t('cloudStorage.linkSyncFolder')}</h2>
            <p className="cloud-picker-subtitle">{t('cloudStorage.linkSyncFolderSubtitle')}</p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onCancel}
            aria-label={t('common.close')}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Provider Tabs */}
        {connectedProviders.length > 0 ? (
          <>
            <div className="cloud-picker-tabs">
              {connectedProviders.map((provider) => (
                <button
                  key={provider.key}
                  type="button"
                  className={`cloud-picker-tab ${activeProvider === provider.key ? 'active' : ''}`}
                  onClick={() => handleProviderChange(provider.key)}
                >
                  {provider.label}
                </button>
              ))}
            </div>

            {/* Breadcrumbs */}
            <div className="cloud-picker-breadcrumbs">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <span className="breadcrumb-separator">/</span>}
                  <button
                    type="button"
                    className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
                    onClick={() => handleBreadcrumbClick(index)}
                  >
                    {crumb.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Content Area */}
            <div className="cloud-picker-content">
              {needsRead ? (
                <GoogleAccessPrompt
                  access="read"
                  reason="browse"
                  variant="card"
                  onGranted={gate.reset}
                />
              ) : loadingContent || accessPending ? (
                <div className="cloud-picker-loading">
                  <Spin size="large" />
                </div>
              ) : folders.length === 0 ? (
                <div className="cloud-picker-empty">
                  <p>{t('cloudStorage.noSubfolders')}</p>
                </div>
              ) : (
                folders.map((folder) => (
                  <div
                    key={folder.folder_id}
                    className={`cloud-picker-item cloud-picker-folder ${
                      selectedFolder?.folder_id === folder.folder_id ? 'selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="sync-folder"
                      checked={selectedFolder?.folder_id === folder.folder_id}
                      onChange={() => handleFolderSelect(folder)}
                      className="cloud-picker-checkbox"
                    />
                    <div
                      className="cloud-picker-item-icon folder-icon"
                      onClick={() => handleFolderSelect(folder)}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                    <span
                      className="cloud-picker-item-name"
                      onClick={() => handleFolderSelect(folder)}
                      style={{ flex: 1, cursor: 'pointer' }}
                    >
                      {folder.name}
                    </span>
                    <button
                      type="button"
                      className="folder-navigate-btn"
                      onClick={() => handleFolderNavigate(folder)}
                      title={t('cloudStorage.openFolder')}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="cloud-picker-footer">
              <span className="cloud-picker-selection-count" dir="auto">
                {selectedFolder ? selectedFolder.name : t('cloudStorage.noFolderSelected')}
              </span>
              <div className="cloud-picker-footer-actions">
                <button type="button" className="btn-cancel" onClick={onCancel}>
                  {t('vault.cancel')}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleConfirm}
                  disabled={!selectedFolder}
                >
                  {t('cloudStorage.linkFolder')}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="cloud-picker-no-connections">
            <div className="cloud-picker-no-connections-icon">
              <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
              </svg>
            </div>
            <h3>{t('cloudStorage.noCloudConnected')}</h3>
            <p>{t('cloudStorage.connectToLinkFolders')}</p>
            <button type="button" className="btn-cancel" onClick={onCancel}>
              {t('common.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
